import './hud.css';
import * as THREE from 'three';
import { summonCost, type PlayerState } from '@orb/sim';

/**
 * The seal's reading (HUD v2): beside the seal, its charge over what the next summon costs —
 * «Заряд 2,4 / 4 · заряджається»; when the charge covers it, «готово · призов» and an ember glow. The 3D
 * arc on the seal shows the same fill; this is the number. Anchored like the skill medallions:
 * projected with the steady camera, written only when something changed.
 */
const fmt = (v: number) => (Math.floor(v * 10) / 10).toLocaleString('uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const _p = new THREE.Vector3();
/** A keyboard at hand: the ready state names the key (Space summons, as 1 casts the skill). */
const KEYBOARD = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;

export class SealDial {
  readonly el: HTMLDivElement;
  private readonly value: HTMLElement;
  private readonly cost: HTMLElement;
  private readonly state: HTMLElement;
  private width = innerWidth; private height = innerHeight;
  private last = '';
  private ready = false;

  constructor(host: HTMLElement, private readonly player: () => PlayerState, private readonly seal: THREE.Vector3) {
    this.el = document.createElement('div');
    this.el.className = 'seal-dial';
    this.el.setAttribute('role', 'status');
    this.el.innerHTML = '<span class="seal-dial-label">Заряд</span><b class="seal-dial-value"></b><b class="seal-dial-cost"></b><i></i><span class="seal-dial-state"></span>';
    this.value = this.el.querySelector('.seal-dial-value')!;
    this.cost = this.el.querySelector('.seal-dial-cost')!;
    this.state = this.el.querySelector('.seal-dial-state')!;
    host.append(this.el);
    const size = () => { this.width = host.clientWidth || innerWidth; this.height = host.clientHeight || innerHeight; };
    size(); new ResizeObserver(size).observe(host);
  }

  update(camera: THREE.Camera) {
    // wide: beside the seal's ring, to the right (the tips and the skills keep the rest); phones: just under it, compact
    const compact = this.width < 560;
    if (compact !== this.el.classList.contains('compact')) { this.el.classList.toggle('compact', compact); this.last = ''; }
    if (compact) _p.set(this.seal.x + 0.35, 0.3, this.seal.z + 0.9); else _p.set(this.seal.x + 1.05, 0.3, this.seal.z);
    _p.project(camera);
    const left = Math.round((_p.x + 1) * this.width / 2), top = Math.round((1 - _p.y) * this.height / 2);
    const p = this.player(), cost = summonCost(p), ready = p.sp >= cost;
    const key = `${left},${top},${fmt(p.sp)},${cost},${ready}`;
    if (key === this.last) return;
    this.last = key;
    this.el.style.left = left + 'px'; this.el.style.top = top + 'px';
    this.value.textContent = fmt(p.sp);
    this.cost.textContent = `/ ${cost}`;
    this.state.textContent = ready ? (compact ? 'готово' : KEYBOARD ? 'готово · Space' : 'готово · призов') : 'заряджається';
    this.el.setAttribute('aria-label', `Заряд печатки ${fmt(p.sp)} з ${cost} на призов${ready ? ', готово' : ''}`);
    if (ready !== this.ready) {
      this.ready = ready;
      this.el.toggleAttribute('data-ready', ready);
      if (ready) { this.el.classList.remove('flare'); void this.el.offsetWidth; this.el.classList.add('flare'); }
    }
    this.el.dataset.position = '1';
  }
}
